import React from "react";
import { cn } from "../../lib/utils";
import { Loader2Icon } from "lucide-react";
import { TestRun } from "../../model_gen/evaluation";
import { ProgressData } from "./useTestRunDetail";

interface Props {
  testRun: TestRun;
  progress: ProgressData | null;
}

const TestRunStatusBadge: React.FC<Props> = ({ testRun, progress }) => {
  const isRunning = ["RUNNING", "PENDING", "CREATED"].includes(
    testRun.status?.toUpperCase() || ""
  );

  if (isRunning && progress) {
    const total = progress.total_requests || 0;
    const inferDone =
      (progress.completed_requests || 0) + (progress.error_requests || 0);
    const evalDone = progress.completed_evals || 0;
    const isEvalPhase = total > 0 && inferDone >= total;

    const phaseDone = isEvalPhase ? evalDone : inferDone;
    const pct = total > 0 ? Math.round((phaseDone / total) * 100) : 0;
    const phaseLabel = isEvalPhase ? "Running Evaluations" : "Running Inference";

    return (
      <div className="flex items-center gap-3">
        <Loader2Icon className="w-4 h-4 animate-spin text-maitai-green" />
        <div className="min-w-[180px]">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-gc-med text-muted-foreground">
              {total > 0
                ? `${phaseLabel} — ${phaseDone} / ${total}`
                : "Starting…"}
            </span>
            {total > 0 && (
              <span className="text-xs font-gc-med text-maitai-green ml-2">
                {pct}%
              </span>
            )}
          </div>
          {total > 0 && (
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div
                className="h-full rounded-full bg-maitai-green transition-all duration-700"
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        testRun.status === "COMPLETED" &&
          "bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200",
        testRun.status === "ERROR" &&
          "bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200",
        testRun.status === "PENDING" &&
          "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200",
        !testRun.status &&
          "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900 dark:text-gray-200"
      )}
    >
      {testRun.status}
    </span>
  );
};

export default TestRunStatusBadge;
